import { useEffect, useState, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  getAdditionalUserInfo,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth, provider, db } from './firebase-config';
import { collection, setDoc, doc, updateDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { Route, Routes, useNavigate } from 'react-router-dom';
import './style.css';

import Loading from './components/pages/Loading';
import Home from './components/pages/Home';
import Login from './components/pages/Login';
import SignUp from './components/pages/SignUp';
import ConfirmationPage from './components/pages/ConfirmationPage';
import MyLists from './components/pages/MyLists';
import ListViewer from './components/pages/ListViewer';
import LandingPage from './components/pages/LandingPage';
import BusinessFinder from './components/pages/BusinessFinder';
import PeopleFinder from './components/pages/PeopleFinder';
import Navbar from './components/Navbar';
import { useAuthState } from 'react-firebase-hooks/auth';
import Pricing from './components/pages/Pricing';
import AccountPage from './components/pages/AccountPage';
import MfaPrompt from './components/MfaPrompt';
import HelpMenu from './components/HelpMenu';
import HelpCenter from './components/pages/HelpCenter';

function App() {
  const [signUpEmail, setSignUpEmail]       = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [loginEmail, setLoginEmail]         = useState('');
  const [loginPassword, setLoginPassword]   = useState('');
  const [credits, setCredits]               = useState(0);
  const [accountType, setAccountType]       = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);

  const navigate          = useNavigate();
  const [user, loading]   = useAuthState(auth);

  const creditCache = useRef(null);

  /* ────────────────────────── auth state router guard ────────────────────────── */
  useEffect(() => {
    if (loading) {
      navigate('/loading');
    }
  }, [loading, navigate]);

  /* ────────────────────────── pull profile on login ────────────────────────── */
  useEffect(() => {
    if (!user) return;
    console.log('User logged in:', user);
    if (creditCache.current === null) return;  // skip until first snapshot
    creditCache.current = credits;

    updateDoc(doc(db, 'users', user.uid), {
      currentCredits: credits,
      updatedAt     : serverTimestamp()
    }).catch(console.error);
  }, [credits, user]);

  useEffect(() => {
    if (!user) {
      setCredits(0);
      setAccountType('');
      return () => {};          // no listener to clean up
    }

    const unsub = onSnapshot(doc(db, 'users', user.uid), snap => {
      if (!snap.exists()) return;
      const data = snap.data();

      /* update local state */
      setCredits(data.currentCredits ?? 0);
      setAccountType(data.accountType ?? 'Trial');

      if (data.mfaEnabled) {
        const done = localStorage.getItem(`mfa:${user.uid}`) === 'true';
        setNeedsMfa(!done);
      } else {
        setNeedsMfa(false);
      }

      /* refresh cache so push-effect doesn’t echo */
      creditCache.current = data.currentCredits ?? 0;
    });

    return unsub;               // detach listener on logout
  }, [user]);

  /* ────────────────────────── helpers ────────────────────────── */
  const seedProfile = async (firebaseUser) => {
    const profile = {
      uid           : firebaseUser.uid,
      email         : firebaseUser.email,
      name          : firebaseUser.displayName ?? '',
      provider      : firebaseUser.providerData[0]?.providerId ?? 'password',
      photoUrl      : firebaseUser.photoURL ?? '',
      accountType   : 'Trial',
      currentCredits: 5,
      createdAt     : serverTimestamp()
    };
    await setDoc(doc(db, 'users', firebaseUser.uid), profile);
  };

  const signUp = async () => {
    try {
      const { user: fbUser } =
        await createUserWithEmailAndPassword(auth, signUpEmail, signUpPassword);
      await seedProfile(fbUser);
    } catch (err) { console.error(err.message); }
  };

  // ─── Forgot-password helper ───
  const sendReset = async () => {
    if (!loginEmail) {
      return alert('Please enter your email address first.');
    }
    try {
      await sendPasswordResetEmail(auth, loginEmail);
      alert('Password reset email sent. Check your inbox.');
    } catch (err) {
      console.error(err);
      alert('Failed to send reset email: ' + err.message);
    }
  };

  const loginWithEmail = async () => {
    try {
      const credential =
        await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      if (getAdditionalUserInfo(credential).isNewUser) await seedProfile(credential.user);
    } catch (err) { console.error(err.message); }
  };

  const loginWithGoogle = async () => {
    try {
      const credential = await signInWithPopup(auth, provider);
      if (getAdditionalUserInfo(credential).isNewUser) await seedProfile(credential.user);
    } catch (err) { console.error(err.message); }
  };

  const logout = async () => { await signOut(auth); };

  /* ────────────────────────── render ────────────────────────── */
  return (
    <div className={user ? 'App signedin' : 'App'}>
      {!loading && (
        <Navbar user={user} logout={logout} credits={credits} />
      )}

      {!loading && needsMfa && user && (
        <MfaPrompt
          uid={user.uid}
          onSuccess={() => setNeedsMfa(false)}
          onCancel={async () => {
            await signOut(auth);
            localStorage.removeItem(`mfa:${user.uid}`);
          }}
        />
      )}

      <Routes>
        <Route
          path="/"
          element={
            !user
              ? <LandingPage />
              : needsMfa
                ? <Loading />
                : <Home />
          }
        />
        <Route path="/login"      element={
          <Login {...{
            loginEmail, setLoginEmail,
            loginPassword, setLoginPassword,
            loginWithEmail, loginWithGoogle,
            sendReset
          }}/>
        }/>
        <Route path="/signup"     element={
          <SignUp {...{
            signUpEmail, setSignUpEmail,
            signUpPassword, setSignUpPassword,
            loginWithGoogle, signUp
          }}/>
        }/>
        <Route path="/business-finder"
               element={<BusinessFinder credits={credits} setCredits={setCredits} accountType={accountType}/>}/>
        <Route path="/people-finder"
               element={<PeopleFinder   credits={credits} setCredits={setCredits} accountType={accountType}/>}/>
        <Route path="/loading"    element={<Loading />} />
        <Route path="/pricing"    element={<Pricing user={user} />} />
        <Route path="/confirmation" element={<ConfirmationPage />} />
        <Route path="/my-lists"   element={<MyLists />} />
        <Route path="/list-viewer/:type/:id" element={<ListViewer accountType={accountType} />} />
        <Route
          path="/account"
          element={
            <AccountPage
              user={user}
              accountType={accountType}
              credits={credits}
            />
          }
        />
        <Route path="/help-center" element={<HelpCenter />} />
      </Routes>
      <HelpMenu user={user} />
    </div>
  );
}

export default App;